// skipcq: JS-W1028, JS-0128
import { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import useReviewPhotoVariantUrl from '../../hooks/useReviewPhotoVariantUrl';
import { buildReviewPhotoSlots } from '../../utils/reviewPhotoFields';
import ReviewPhotoLightbox from './ReviewPhotoLightbox';

function ReadyThumbnailButton({
  reviewType,
  reviewId,
  photoId,
  onOpen,
}) {
  const { url, loading, error } = useReviewPhotoVariantUrl({
    reviewType,
    reviewId,
    photoId,
    variantName: 'thumb',
    enabled: true,
  });

  const handleClick = useCallback(() => {
    onOpen(photoId);
  }, [onOpen, photoId]);

  if (loading) {
    return (
      <Box
        sx={{
          width: 72,
          height: 72,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'action.hover',
          borderRadius: 1,
        }}
      >
        <CircularProgress size={20} aria-label="Loading thumbnail" />
      </Box>
    );
  }

  if (error || !url) {
    return (
      <Box
        sx={{
          width: 72,
          height: 72,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'action.hover',
          borderRadius: 1,
          px: 0.5,
        }}
      >
        <Typography variant="caption" color="text.secondary" align="center">
          Unavailable
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      component="button"
      type="button"
      onClick={handleClick}
      aria-label="View review photo"
      sx={{
        p: 0,
        border: 'none',
        background: 'none',
        cursor: 'pointer',
        borderRadius: 1,
        overflow: 'hidden',
        width: 72,
        height: 72,
      }}
    >
      <Box
        component="img"
        src={url}
        alt=""
        sx={{
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          display: 'block',
        }}
      />
    </Box>
  );
}

ReadyThumbnailButton.propTypes = {
  reviewType: PropTypes.oneOf(['beer', 'hospitality']).isRequired,
  reviewId: PropTypes.string.isRequired,
  photoId: PropTypes.string.isRequired,
  onOpen: PropTypes.func.isRequired,
};

function ProcessingSlot() {
  return (
    <Box
      sx={{
        width: 72,
        height: 72,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'action.hover',
        borderRadius: 1,
        px: 0.5,
      }}
    >
      <Typography variant="caption" color="text.secondary" align="center">
        Processing
      </Typography>
    </Box>
  );
}

function FailedSlot() {
  return (
    <Box
      sx={{
        width: 72,
        height: 72,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'action.hover',
        borderRadius: 1,
        px: 0.5,
      }}
    >
      <Typography variant="caption" color="text.secondary" align="center">
        Unavailable
      </Typography>
    </Box>
  );
}

function PhotoSlot({ slot, reviewType, reviewId, onOpenReady }) {
  if (slot.status === 'ready') {
    return (
      <ReadyThumbnailButton
        reviewType={reviewType}
        reviewId={reviewId}
        photoId={slot.photoId}
        onOpen={onOpenReady}
      />
    );
  }
  if (slot.status === 'failed') {
    return <FailedSlot />;
  }
  return <ProcessingSlot />;
}

PhotoSlot.propTypes = {
  slot: PropTypes.shape({
    photoId: PropTypes.string.isRequired,
    status: PropTypes.oneOf(['processing', 'ready', 'failed']).isRequired,
  }).isRequired,
  reviewType: PropTypes.oneOf(['beer', 'hospitality']).isRequired,
  reviewId: PropTypes.string.isRequired,
  onOpenReady: PropTypes.func.isRequired,
};

/**
 * Ordered review photo thumbnails: after comment, before reaction dock.
 * Display-only (no upload/create).
 */
function ReviewPhotosStrip({
  reviewType,
  reviewId = '',
  photoIds = [],
  photoSummary = {},
}) {
  const slots = useMemo(
    () => buildReviewPhotoSlots(photoIds, photoSummary),
    [photoIds, photoSummary],
  );
  const readyPhotoIds = useMemo(
    () => slots.filter((slot) => slot.status === 'ready').map((slot) => slot.photoId),
    [slots],
  );
  const hasProcessing = slots.some((slot) => slot.status === 'processing');
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxPhotoId, setLightboxPhotoId] = useState(null);

  const handleOpenReady = useCallback((photoId) => {
    setLightboxPhotoId(photoId);
    setLightboxOpen(true);
  }, []);

  const handleCloseLightbox = useCallback(() => {
    setLightboxOpen(false);
  }, []);

  if (slots.length === 0 || !reviewId) {
    return null;
  }

  return (
    <Box sx={{ pt: 1 }} data-testid="review-photos-strip">
      {hasProcessing ? (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Photos processing
        </Typography>
      ) : null}
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        {slots.map((slot) => (
          <PhotoSlot
            key={slot.photoId}
            slot={slot}
            reviewType={reviewType}
            reviewId={reviewId}
            onOpenReady={handleOpenReady}
          />
        ))}
      </Stack>
      <ReviewPhotoLightbox
        key={`${lightboxOpen ? 'open' : 'closed'}-${lightboxPhotoId || 'none'}`}
        open={lightboxOpen}
        onClose={handleCloseLightbox}
        reviewType={reviewType}
        reviewId={reviewId}
        readyPhotoIds={readyPhotoIds}
        initialPhotoId={lightboxPhotoId}
      />
    </Box>
  );
}

ReviewPhotosStrip.propTypes = {
  reviewType: PropTypes.oneOf(['beer', 'hospitality']).isRequired,
  reviewId: PropTypes.string,
  photoIds: PropTypes.arrayOf(PropTypes.string),
  photoSummary: PropTypes.objectOf(
    PropTypes.shape({
      status: PropTypes.oneOf(['processing', 'ready', 'failed']).isRequired,
      failureCode: PropTypes.string,
      width: PropTypes.number,
      height: PropTypes.number,
      variantVersion: PropTypes.number,
    }),
  ),
};

export default ReviewPhotosStrip;
